import { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { getThemeColors, ACCENTS, SURFACES } from "./data/themes";
import { makeStyles } from "./utils/css";
import { NavIcons, Toast, ForgeLogo, ForgeTitle } from "./components/Primitives";
import storage from "./utils/storage";
import { AuthProvider, useAuth } from "./context/AuthContext";
import CONFIG from "./data/config";

import WorkoutPlayer from "./components/WorkoutPlayer";
import CoachPanel, { CoachFAB } from "./components/CoachPanel";
import TodayView from "./views/TodayView";
import DataView from "./views/DataView";
import GuideView from "./views/GuideView";
import ProgramView from "./views/ProgramView";
import VolumeLog from "./views/VolumeLog";
import CheckIn from "./views/CheckIn";
import SettingsView from "./views/SettingsView";
import LoginScreen from "./views/LoginScreen";
import OnboardingScreen from "./views/OnboardingScreen";
import Walkthrough from "./components/Walkthrough";
import PosingView from "./views/PosingView";
import ProgramPDF from "./views/ProgramPDF";
import DocumentsView from "./views/DocumentsView";
import CardioLog from "./views/CardioLog";
import MobilityView from "./views/MobilityView";
import CalendarView from "./views/CalendarView";

// ─── INNER APP ────────────────────────────────────────────────
function AppInner() {
  const { isLoggedIn, isLoading, logout, user } = useAuth();

  const [accentId, setAccentId] = useState(() => storage.get("accent", "forge"));
  const [surfaceId, setSurfaceId] = useState(() => {
    const s = storage.get("surface", "void");
    if (s === "slate") { storage.set("surface", "titanium"); return "titanium"; }
    return s;
  });
  const [fontScale, setFontScale] = useState(() => storage.get("font_scale", 1));
  const C = getThemeColors(accentId, surfaceId);
  const css = makeStyles(C, fontScale);

  // Navigation state — no router, just useState
  const [screen, setScreen] = useState("app"); // "login" | "onboarding" | "app"
  const [tab, setTab] = useState("today");
  const [view, setView] = useState("main");
  const [workoutDay, setWorkoutDay] = useState(null);
  const [toast, setToast] = useState(null);
  const [coachOpen, setCoachOpen] = useState(false);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [splashDone, setSplashDone] = useState(() => !!sessionStorage.getItem("forge_splash"));
  const [splashFading, setSplashFading] = useState(false);
  // Post-onboarding welcome splash (name shown for ~2.5s before landing in Today)
  const [welcomeName, setWelcomeName] = useState(null);

  // Toast system
  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }, []);

  // Splash screen
  useEffect(() => {
    if (!splashDone && !isLoggedIn && !isLoading) {
      const fadeTimer = setTimeout(() => setSplashFading(true), 1800);
      const doneTimer = setTimeout(() => {
        setSplashDone(true);
        sessionStorage.setItem("forge_splash", "1");
      }, 2200);
      return () => { clearTimeout(fadeTimer); clearTimeout(doneTimer); };
    }
    if (!splashDone && (isLoggedIn || isLoading)) setSplashDone(true);
  }, [isLoggedIn, isLoading]);

  // Version check — detect stale cached versions
  const [updateAvailable, setUpdateAvailable] = useState(false);
  useEffect(() => {
    if (!isLoggedIn) return;
    const checkVersion = () => {
      const path = window.location.pathname;
      const match = path.match(/^(.*\/WebObjects\/forge\.woa)/);
      const base = match ? match[1] : '';
      fetch(`${base}/wa/appApi?action=version`).then(r => r.json()).then(data => {
        if (data.version && data.version !== CONFIG.version) setUpdateAvailable(true);
      }).catch(() => {});
    };
    checkVersion();
    const interval = setInterval(checkVersion, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isLoggedIn]);

  const changeAccent = (id) => {
    setAccentId(id);
    storage.set("accent", id);
    // Update apple-touch-icon and theme-color to match accent
    const accentColors = { forge: "00FF88", platinum: "A8B8C8", obsidian: "a78bfa", ember: "ff8c42", arctic: "64b5f6", crimson: "ef5350", gold: "ffd54f", rose: "f48fb1" };
    const color = accentColors[id] || "00FF88";
    const iconLink = document.querySelector('link[rel="apple-touch-icon"]');
    if (iconLink) iconLink.href = `/wa/portalIcon?color=${color}`;
  };
  const changeSurface = (id) => { setSurfaceId(id); storage.set("surface", id); };
  const changeFontScale = (scale) => { setFontScale(scale); storage.set("font_scale", scale); };

  // Set icon color on mount based on stored accent
  useEffect(() => {
    const accentColors = { forge: "00FF88", platinum: "A8B8C8", obsidian: "a78bfa", ember: "ff8c42", arctic: "64b5f6", crimson: "ef5350", gold: "ffd54f", rose: "f48fb1" };
    const color = accentColors[accentId] || "00FF88";
    const iconLink = document.querySelector('link[rel="apple-touch-icon"]');
    if (iconLink) iconLink.href = `/wa/portalIcon?color=${color}`;
  }, []);

  // Scroll to top on navigation
  const scrollRef = useRef(null);
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, []);

  useLayoutEffect(() => { scrollToTop(); }, [view, tab]);

  const startWorkout = (day) => { setWorkoutDay(day); setView("wp"); };
  const exitWorkout = () => { setWorkoutDay(null); setView("main"); setTab("today"); scrollToTop(); };
  const goMain = () => { setView("main"); scrollToTop(); };

  const icons = NavIcons();

  const isMainTab = view === "main";

  // ─── LOADING ──────────────────────────────────────────────────
  if (isLoading) {
    return (
      <>
        <div style={{
          minHeight: "100vh", background: C.bgGradient || C.bg, display: "flex",
          alignItems: "center", justifyContent: "center",
        }}>
          <ForgeLogo C={C} size="lg" />
        </div>
        <style>{css}</style>
      </>
    );
  }

  // ─── SPLASH SCREEN ──────────────────────────────────────────
  if (!splashDone && !isLoggedIn) {
    return (
      <>
        <div style={{
          minHeight: "100vh", background: "#010102", display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden",
          ...(splashFading ? { animation: "splashFadeOut 0.4s ease forwards" } : {}),
        }}>
          {/* Deep void vignette */}
          <div style={{
            position: "absolute", inset: 0, pointerEvents: "none",
            background: "radial-gradient(ellipse 50% 50% at 50% 50%, transparent 0%, rgba(0,0,0,0.7) 100%)",
          }} />
          {/* Core glow */}
          <div style={{
            position: "absolute", top: "50%", left: "50%", width: 600, height: 600, borderRadius: "50%",
            background: `radial-gradient(circle, ${C.accent020} 0%, ${C.accent008} 20%, transparent 55%)`,
            transform: "translate(-50%, -50%)",
            animation: "orbFloat 6s ease-in-out infinite",
            pointerEvents: "none",
          }} />
          {/* Orbital ring */}
          <div style={{
            position: "absolute", top: "50%", left: "50%", width: 300, height: 300,
            border: `0.5px solid ${C.accent010}`, borderRadius: "50%",
            transform: "translate(-50%, -50%)",
            animation: "splashOrbit 30s linear infinite",
            pointerEvents: "none",
            boxShadow: `0 0 20px ${C.accent005}`,
          }}>
            <div style={{
              position: "absolute", top: -2, left: "50%", width: 4, height: 4, borderRadius: "50%",
              background: C.accent, transform: "translateX(-50%)",
              boxShadow: `0 0 10px ${C.accent040}`,
            }} />
          </div>
          {/* Vertical beam */}
          <div style={{
            position: "absolute", top: 0, left: "50%", width: 1, height: "100%",
            background: `linear-gradient(180deg, transparent 10%, ${C.accent008} 40%, ${C.accent015} 50%, ${C.accent008} 60%, transparent 90%)`,
            transform: "translateX(-50%)", pointerEvents: "none",
          }} />
          <div style={{ animation: "logoGlowRise 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards", willChange: "transform, opacity, filter", position: "relative", zIndex: 1 }}>
            <ForgeLogo C={C} size="lg" />
          </div>
          <div style={{
            height: 1, background: C.dividerGrad,
            animation: "lineGrow 0.6s ease 0.8s both", marginTop: 20,
            position: "relative", zIndex: 1,
          }} />
          <div style={{
            fontSize: 'var(--fs-2xs)', color: C.text4, fontFamily: "var(--m)",
            letterSpacing: ".3em", marginTop: 16,
            animation: "badgeFade 0.5s ease 1.1s both",
            position: "relative", zIndex: 1,
          }}>
            fitnessforge.ai
          </div>
        </div>
        <style>{`
          @keyframes splashOrbit {
            from { transform: translate(-50%, -50%) rotate(0deg); }
            to { transform: translate(-50%, -50%) rotate(360deg); }
          }
          ${css}
        `}</style>
      </>
    );
  }

  // ─── LOGIN ──────────────────────────────────────────────────
  // Check for password reset token in URL
  const resetToken = new URLSearchParams(window.location.search).get("resetToken");
  if (!isLoggedIn || resetToken) {
    return (
      <>
        <LoginScreen C={C} resetToken={resetToken} onLogin={(clientData) => {
          // Clear resetToken from URL
          if (resetToken) {
            window.history.replaceState({}, '', window.location.pathname + window.location.hash);
          }
          const client = clientData || user;
          setScreen((client?.intakeCompleted || storage.get("intake_completed")) ? "app" : "onboarding");
        }} />
        <style>{css}</style>
      </>
    );
  }

  // ─── ONBOARDING ─────────────────────────────────────────────
  if (screen === "onboarding") {
    return (
      <>
        <OnboardingScreen C={C} onComplete={() => {
          const name = user?.firstName || "athlete";
          setWelcomeName(name);
          setTimeout(() => setWelcomeName(null), 2600);
          setScreen("app");
          scrollToTop();
          if (!storage.get("wt_done")) setShowWalkthrough(true);
        }} changeAccent={changeAccent} changeSurface={changeSurface} />
        <style>{css}</style>
      </>
    );
  }

  // ─── WORKOUT PLAYER (full screen takeover) ──────────────────
  if (view === "wp" && workoutDay) {
    return (
      <>
        <WorkoutPlayer day={workoutDay} onExit={exitWorkout} C={C} showToast={showToast} coachOpen={coachOpen} />
        {!coachOpen && (
          <div style={{ animation: "coachFabIn 0.3s ease" }}>
            <CoachFAB C={C} onClick={() => setCoachOpen(true)} />
          </div>
        )}
        {coachOpen && <CoachPanel C={C} isOverlay onClose={() => setCoachOpen(false)} isWorkout />}
        <style>{css}</style>
      </>
    );
  }

  // ─── TAB CONTENT ────────────────────────────────────────────
  const onNav = (v) => { setView(v); scrollToTop(); };

  const content = () => {
    // Sub-views (overlays that go back to main)
    if (view === "gd") return <GuideView C={C} onBack={goMain} />;
    if (view === "vl") return <VolumeLog C={C} onBack={goMain} />;
    if (view === "ci") return <CheckIn C={C} onBack={goMain} />;
    if (view === "pp") return <CheckIn C={C} onBack={goMain} initialTab="photos" />;
    if (view === "posing") return <PosingView C={C} onBack={goMain} />;
    if (view === "cardio") return <CardioLog C={C} onBack={goMain} />;
    if (view === "mobility") return <MobilityView C={C} onBack={goMain} />;
    if (view === "compare") return <CheckIn C={C} onBack={goMain} initialTab="photos" />;
    if (view === "calendar") return <CalendarView C={C} onBack={goMain} onWork={startWorkout} />;
    if (view === "pdf") return <ProgramPDF C={C} onClose={goMain} />;
    if (view === "docs") return <DocumentsView C={C} onBack={goMain} onNav={onNav} />;
    if (view === "data") return <DataView C={C} onNav={onNav} onBack={goMain} />;
    if (view === "settings") return (
      <SettingsView
        C={C} accentId={accentId} surfaceId={surfaceId}
        changeAccent={changeAccent} changeSurface={changeSurface}
        fontScale={fontScale} changeFontScale={changeFontScale}
        showToast={showToast} onBack={goMain}
        onLogout={() => { logout(); setScreen("app"); }}
      />
    );

    // Main tabs rendered persistently below, not here
    return null;
  };

  // 4 tabs
  const tabs = [
    { k: "today", l: "Today" },
    { k: "coach", l: "Coach" },
    { k: "program", l: "Program" },
    { k: "profile", l: "Profile" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: C.bgGradient || C.bg, fontFamily: "var(--b)", color: C.text1 }}>
      {/* ─── ATMOSPHERIC BACKGROUND ─── */}
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0 }}>
        <div style={{
          position: "absolute", top: "-20%", left: "50%", transform: "translateX(-50%)",
          width: "140%", height: "70%",
          background: C.atmosphereGrad,
        }} />
        {C.atmosphereGrad2 && <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0, height: "40%",
          background: C.atmosphereGrad2,
        }} />}
        <div className="forge-orb" style={{
          position: "absolute", top: "10%", left: "50%",
          width: 700, height: 700, borderRadius: "50%",
          background: `radial-gradient(circle, ${C.atmosphereOrb} 0%, transparent 55%)`,
          animation: "orbFloat 8s ease-in-out infinite",
          pointerEvents: "none",
        }} />
        <div className="forge-orb" style={{
          position: "absolute", top: "55%", left: "25%",
          width: 500, height: 500, borderRadius: "50%",
          background: `radial-gradient(circle, ${C.atmosphereOrb} 0%, transparent 55%)`,
          animation: "orbFloat2 10s ease-in-out infinite",
          pointerEvents: "none",
        }} />
      </div>

      <div className="forge-app-wrapper" style={{ maxWidth: 'var(--content-max)', margin: "0 auto", minHeight: "100vh", position: "relative", zIndex: 1 }}>
        {/* ─── HEADER ─── */}
        <div
          className="forge-header forge-header-blur"
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "calc(16px + env(safe-area-inset-top, 0px)) 16px 14px",
            borderBottom: "none",
            position: "sticky", top: 0,
            background: C.headerBg,
            backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
            zIndex: 10,
          }}
        >
          {/* Neon green border — brand-locked bottom edge */}
          <div style={{
            position: "absolute", bottom: 0, left: 0, right: 0, height: 1,
            background: C.dividerGrad,
            boxShadow: `0 1px 12px ${C.accent020}, 0 0 4px ${C.accent015}`,
          }} />
          <div onClick={() => { setTab("today"); setView("main"); scrollToTop(); }} style={{ display: "flex", alignItems: "center", gap: 'var(--gap-lg)', cursor: "pointer", WebkitTapHighlightColor: "transparent" }}>
            <ForgeLogo C={C} size="md" />
            <div>
              <div><ForgeTitle C={C} size={18} /></div>
              <div style={{
                fontSize: 'var(--fs-2xs)', color: C.text4, fontFamily: "var(--m)",
                letterSpacing: ".22em", marginTop: 1,
              }}>
                fitnessforge.ai
              </div>
            </div>
          </div>
          <div style={{ width: 40, height: 40 }} />
        </div>

        {/* ─── SUB-VIEW CONTENT (overlays that go back to main) ─── */}
        {view !== "main" && (
          <div
            ref={scrollRef}
            className="forge-content"
            style={{ padding: "20px 16px 110px", position: "relative", zIndex: 1 }}
          >
            {content()}
          </div>
        )}

        {/* ─── PERSISTENT TABS (stay mounted, toggle visibility) ─── */}
        <div style={{ display: view === "main" && tab === "today" ? "block" : "none", padding: "20px 16px 110px", position: "relative", zIndex: 1 }}>
          <TodayView C={C} onWork={startWorkout} onNav={onNav} showToast={showToast} />
        </div>
        <div style={{ display: view === "main" && tab === "coach" ? "block" : "none", padding: "0 16px 110px", position: "relative", zIndex: 1 }}>
          <CoachPanel C={C} isOverlay={false} visible={view === "main" && tab === "coach"} />
        </div>
        <div style={{ display: view === "main" && tab === "program" ? "block" : "none", padding: "20px 16px 110px", position: "relative", zIndex: 1 }}>
          <ProgramView C={C} onWork={startWorkout} onNav={onNav} />
        </div>
        <div style={{ display: view === "main" && tab === "profile" ? "block" : "none", padding: "20px 16px 110px", position: "relative", zIndex: 1 }}>
          <SettingsView
            C={C} accentId={accentId} surfaceId={surfaceId}
            changeAccent={changeAccent} changeSurface={changeSurface}
            fontScale={fontScale} changeFontScale={changeFontScale}
            showToast={showToast} onNav={onNav}
            onLogout={() => { logout(); setScreen("app"); }}
          />
        </div>

        {/* ─── COACH OVERLAY ─── */}
        {coachOpen && <CoachPanel C={C} isOverlay onClose={() => setCoachOpen(false)} />}

        {/* ─── TOAST ─── */}
        {toast && <Toast message={toast} C={C} />}

        {/* ─── UPDATE AVAILABLE BANNER ─── */}
        {updateAvailable && (
          <div onClick={() => window.location.reload(true)} style={{
            position: "fixed", top: 12, left: "50%", transform: "translateX(-50%)",
            background: C.accent, color: "#000", padding: "10px 20px", borderRadius: 10,
            fontSize: "var(--fs-sm)", fontWeight: 700, fontFamily: "var(--m)", letterSpacing: ".08em",
            cursor: "pointer", zIndex: 200, boxShadow: `0 4px 20px ${C.accent040}`,
            animation: "fadeIn 0.4s ease",
          }}>
            UPDATE AVAILABLE — TAP TO REFRESH
          </div>
        )}

        {/* ─── BOTTOM NAVIGATION — 4 TABS ─── */}
        {isLoggedIn && (
          <div
            className={`forge-nav forge-nav-blur${!isMainTab ? ' forge-nav-subpage' : ''}`}
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              maxWidth: 'var(--content-max)',
              margin: "0 auto",
              background: C.navBg,
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              borderTop: "none",
              display: "flex",
              justifyContent: "space-around",
              padding: "8px 0 max(8px, env(safe-area-inset-bottom))",
              zIndex: 30,
            }}
          >
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, height: 1,
              background: C.dividerGrad,
              boxShadow: `0 -1px 8px ${C.accent008}`,
            }} />
            {tabs.map((t) => {
              const active = tab === t.k && view === "main";
              return (
                <div
                  key={t.k}
                  className={active ? "forge-nav-active" : undefined}
                  onClick={() => { setTab(t.k); setView("main"); scrollToTop(); }}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 3,
                    padding: "6px 12px",
                    cursor: "pointer",
                    color: active ? C.accent : C.text4,
                    fontSize: 'var(--fs-xs)',
                    fontWeight: 700,
                    fontFamily: "var(--m)",
                    letterSpacing: ".04em",
                    position: "relative",
                    userSelect: "none",
                    transition: "color 0.2s, background 0.2s",
                    minWidth: 52,
                    minHeight: 44,
                    justifyContent: "center",
                  }}
                >
                  {active && (
                    <div style={{
                      position: "absolute", top: -8, left: "50%", transform: "translateX(-50%)",
                      width: 24, height: 2, borderRadius: 1,
                      background: C.accent,
                      boxShadow: `0 0 6px ${C.accent030}`,
                    }} />
                  )}
                  <div style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    filter: active ? `drop-shadow(0 0 3px ${C.accent030})` : "none",
                    transition: "filter 0.2s",
                  }}>
                    {icons[t.k]}
                  </div>
                  <span style={{
                    marginTop: 1,
                    textShadow: active ? `0 0 6px ${C.accent020}` : "none",
                  }}>{t.l}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ─── WALKTHROUGH (first-use tour) ─── */}
      {showWalkthrough && <Walkthrough C={C} onComplete={() => setShowWalkthrough(false)} />}

      {/* ─── POST-ONBOARDING WELCOME SPLASH ─── */}
      {welcomeName && (
        <div
          onClick={() => setWelcomeName(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 500,
            background: C.bgGradient || C.bg,
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            padding: "40px 24px",
            animation: "fadeIn 0.4s ease",
          }}>
          <div style={{ marginBottom: 28, animation: "logoGlowRise 0.7s ease" }}>
            <ForgeLogo C={C} size={72} />
          </div>
          <div style={{
            fontSize: "var(--fs-display)",
            fontFamily: "var(--d)",
            fontWeight: 700,
            color: C.text1,
            textAlign: "center",
            letterSpacing: "0.02em",
            animation: "badgeFade 0.8s ease 0.2s both",
          }}>
            Welcome, {welcomeName}.
          </div>
          <div style={{
            width: 60, height: 1, background: C.accent,
            boxShadow: `0 0 12px ${C.accent060}`,
            margin: "18px 0 16px",
            animation: "lineGrow 0.6s ease 0.5s both",
          }} />
          <div style={{
            fontSize: "var(--fs-md)",
            fontFamily: "var(--m)",
            color: C.text3,
            textAlign: "center",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            maxWidth: 320,
            animation: "badgeFade 0.8s ease 0.7s both",
          }}>
            Your Forge is ready.
          </div>
          <div style={{
            marginTop: 32,
            fontSize: "var(--fs-sm)",
            fontFamily: "var(--m)",
            color: C.text4,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            animation: "badgeFade 0.8s ease 1.3s both",
          }}>
            Tap to begin
          </div>
        </div>
      )}

      <style>{css}</style>
    </div>
  );
}

// ─── ROOT APP ──────────────────────────────────────────────────
import ErrorBoundary from "./components/ErrorBoundary";

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppInner />
      </AuthProvider>
    </ErrorBoundary>
  );
}
